import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useCart } from '../contexts/CartContext'
import { useNavigate } from 'react-router-dom'
import { ShoppingBag, CreditCard, MapPin, User, Tag } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import { orderService, locationsService, paymentService } from '../services/api'
import Currency from '../components/Currency'
import { getFeaturedImageUrl } from '../utils/imageUtils'
import { redirectToUPayments, logUpaymentsUrl } from '../utils/paymentUtils'
import { toast } from 'react-hot-toast'
import '../styles/CartCheckout.css'

const Checkout = () => {
  const { isAuthenticated, loading: authLoading, user } = useAuth()
  const { items, total, clearCart } = useCart()
  const navigate = useNavigate()
  
  const [isProcessing, setIsProcessing] = useState(false)
  const [orderPlaced, setOrderPlaced] = useState(false)
  const [orderNumber, setOrderNumber] = useState('')
  const [error, setError] = useState('')
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('')
  const [promoDiscount, setPromoDiscount] = useState(0)
  const [promoError, setPromoError] = useState('')
  const [applyingPromo, setApplyingPromo] = useState(false)

  // Locations state
  const [countries, setCountries] = useState([])
  const [governorates, setGovernorates] = useState([])
  const [cities, setCities] = useState([])
  const [loadingCountries, setLoadingCountries] = useState(false)
  const [loadingGovernorates, setLoadingGovernorates] = useState(false)
  const [loadingCities, setLoadingCities] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    shipping_address: '',
    country_id: '',
    governorate_id: '',
    city_id: '',
    payment_method: 'cash_on_delivery'
  })

  const [formErrors, setFormErrors] = useState({})

  // Load countries on mount
  useEffect(() => {
    const loadCountries = async () => {
      try {
        setLoadingCountries(true)
        const countriesData = await locationsService.getCountries()
        setCountries(countriesData)
      } catch (error) {
        console.error('Error loading countries:', error)
      } finally {
        setLoadingCountries(false)
      }
    }
    loadCountries()
  }, [])

  // Load governorates when country changes
  useEffect(() => {
    if (!formData.country_id) {
      setGovernorates([])
      setFormData(prev => ({ ...prev, governorate_id: '', city_id: '' }))
      return
    }
    const loadGovernorates = async () => {
      try {
        setLoadingGovernorates(true)
        // Assuming API supports filtering by country
        const governoratesData = await locationsService.getGovernorates(formData.country_id)
        setGovernorates(governoratesData)
      } catch (error) {
        console.error('Error loading governorates:', error)
        setGovernorates([])
      } finally {
        setLoadingGovernorates(false)
      }
    }
    loadGovernorates()
  }, [formData.country_id])

  // Load cities when governorate changes
  useEffect(() => {
    if (!formData.governorate_id) {
      setCities([])
      setFormData(prev => ({ ...prev, city_id: '' }))
      return
    }
    const loadCities = async () => {
      try {
        setLoadingCities(true)
        // Use the new API to get cities by governorate
        const citiesData = await locationsService.getCitiesByGovernorate(formData.governorate_id)
        setCities(citiesData)
      } catch (error) {
        console.error('Error loading cities:', error)
        setCities([])
      } finally {
        setLoadingCities(false)
      }
    }
    loadCities()
  }, [formData.governorate_id])

  // Load user data when authenticated
  useEffect(() => {
    if (isAuthenticated && user && !authLoading) {
      // Use the user's full name
      const fullName = user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || ''
      
      setFormData(prev => ({
        ...prev,
        name: fullName,
        email: user.email || '',
        phone: user.phone || '',
        shipping_address: user.address || ''
      }))
    }
  }, [isAuthenticated, user, authLoading])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => {
      // Reset dependent fields
      if (name === 'country_id') {
        return { ...prev, country_id: value, governorate_id: '', city_id: '' }
      }
      if (name === 'governorate_id') {
        return { ...prev, governorate_id: value, city_id: '' }
      }
      return { ...prev, [name]: value }
    })
    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }))
    }
  }

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      setPromoError('Please enter a promo code')
      return
    }

    setApplyingPromo(true)
    setPromoError('')
    
    try {
      const response = await fetch(`/api/promo-codes/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          code: promoCode,
          total: cartSubtotal 
        }),
      })
      
      const data = await response.json()
      
      if (response.ok) {
        setPromoDiscount(data.discount)
        toast.success(`Promo code applied! ${data.discount_type === 'percentage' ? `${data.discount_value}%` : `$${data.discount.toFixed(2)}`} discount`)
      } else {
        setPromoError(data.message || 'Invalid promo code')
        setPromoDiscount(0)
      }
    } catch (error) {
      console.error('Error applying promo code:', error)
      setPromoError('Failed to apply promo code')
      setPromoDiscount(0)
    } finally {
      setApplyingPromo(false)
    }
  }

  const handleRemovePromo = () => {
    setPromoCode('')
    setPromoDiscount(0)
    setPromoError('')
  }

  const validateForm = () => {
    const errors = {}
    
    if (!formData.name.trim()) errors.name = 'Name is required'
    if (!formData.email.trim()) errors.email = 'Email is required'
    if (!formData.phone.trim()) errors.phone = 'Phone is required'
    if (!formData.shipping_address.trim()) errors.shipping_address = 'Shipping address is required'
    if (!formData.country_id) errors.country_id = 'Country is required'
    if (!formData.governorate_id) errors.governorate_id = 'Governorate is required'
    if (!formData.city_id) errors.city_id = 'City is required'
    
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Check if cart is empty
    if (!items || items.length === 0) {
      setError('Your cart is empty. Please add items to your cart before checkout.')
      setIsProcessing(false)
      return
    }
    
    // Validate form before submission
    if (!validateForm()) {
      setError('Please fill in all required fields')
      return
    }
    
    setIsProcessing(true)
    setError('')

    try {
      // Get selected city name for the order
      const selectedCity = cities.find(city => city.id == formData.city_id)
      const cityName = selectedCity ? selectedCity.name : 'Unknown City'

      const orderData = {
        user_id: user?.id || null, // null for guest checkout
        total_amount: finalTotal,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        shipping_address: formData.shipping_address,
        country_id: formData.country_id,
        governorate_id: formData.governorate_id,
        city_id: formData.city_id,
        city: cityName, // Add city name for backend
        payment_method: formData.payment_method,
        items: items // Include cart items with all their attributes
      }

      // Add optional fields only if they have values
      if (promoCode && promoCode.trim()) {
        orderData.promo_code = promoCode.trim()
      }
      
      // Handle different payment methods
      if (formData.payment_method === 'upayments') {
        // For UPayments, create order first (like COD) and get payment URL
        const response = await orderService.createOrder(orderData)
        
        if (response && response.order && response.order.payment_url) {
          // Store order data in localStorage for when user returns
          localStorage.setItem('pendingOrder', JSON.stringify({
            ...orderData,
            order_number: response.order.order_number
          }))
          
          // Direct redirect to UPayments
          window.location.href = response.order.payment_url
          return
        } else if (response && response.order && response.order.order_number) {
          // If no payment URL but order created (shouldn't happen for UPayments)
          setOrderNumber(response.order.order_number)
          await clearCart()
          navigate(`/order-status/${response.order.order_number}`)
          return
        } else {
          throw new Error('وسيلة الدفع المختارة غير متاحة حالياً')
        }
      } else {
        // For cash on delivery, create order directly
        const response = await orderService.createOrder(orderData)
        
        if (response && response.order && response.order.order_number) {
          setOrderNumber(response.order.order_number)
          await clearCart()
          // Redirect to order status page
          navigate(`/order-status/${response.order.order_number}`)
        } else {
          throw new Error('Order was created but no order number was returned')
        }
      }
    } catch (error) {
      console.error('Order creation error:', error)
      
      // Check if this is a UPayment failure with cleanup instruction
      if (error.response?.data?.action === 'redirect_to_checkout' && error.response?.data?.cart_preserved) {
        // UPayment failed, order was cleaned up, cart is preserved
        setError(error.response.data.message || 'Payment failed. Please try again.')
        toast.error('فشل في عملية الدفع. يرجى المحاولة مرة أخرى.')
        // Don't clear the cart, let user try again
        setIsProcessing(false)
        return
      }
      
      // Handle different types of errors
      if (error.details && Array.isArray(error.details)) {
        // Validation errors from backend
        const backendErrors = {}
        error.details.forEach(detail => {
          const fieldName = detail.field
          backendErrors[fieldName] = detail.message
        })
        setFormErrors(backendErrors)
        setError(error.message || 'Please correct the highlighted errors')
      } else {
        // General errors
        setError(error.message || error.response?.data?.message || 'Failed to place order')
      }
    } finally {
      setIsProcessing(false)
    }
  }

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    )
  }

  if (!items || items.length === 0 && !orderPlaced) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <ShoppingBag className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Your cart is empty</h2>
          <p className="text-gray-600">Add some products to your cart before checking out.</p>
        </div>
      </div>
    )
  }

  if (orderPlaced) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
            <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="mt-2 text-lg font-medium text-gray-900">Order Placed Successfully!</h3>
          <p className="mt-1 text-sm text-gray-500">
            Order #{orderNumber}
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Thank you for your order. You will receive a confirmation email shortly.
          </p>
        </div>
      </div>
    )
  }

  const cartSubtotal = total
  
  // Get shipping amount based on selected city
  const getShippingAmount = () => {
    if (!formData.city_id) return 0.00 // Default shipping fee when no city selected
    const selectedCity = cities.find(city => city.id == formData.city_id) // Using == for type coercion
    console.log('Selected city ID:', formData.city_id, 'Selected city:', selectedCity)
    return selectedCity?.shipping_fee ? parseFloat(selectedCity.shipping_fee) : 0.00
  }
  const shippingAmount = getShippingAmount()
  const subtotalAfterPromo = cartSubtotal - promoDiscount
  const finalTotal = subtotalAfterPromo + shippingAmount

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">إتمام الطلب</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Order Summary */}
        <div className="lg:col-span-5">
          <div className="checkout-section-card">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium text-gray-900">ملخص الطلب</h2>
            </div>
            <div className="px-6 py-4">
              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                        {getFeaturedImageUrl(item.featured_image) ? (
                          <img
                            src={getFeaturedImageUrl(item.featured_image)}
                            alt={item.name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        ) : (
                          <span className="text-gray-500 text-xs">لا توجد صورة</span>
                        )}
                      </div>
                    </div>
                    <div className="flex-1 px-3">
                      <h4 className="text-sm font-medium text-gray-900">{item.name}</h4>
                      <p className="text-sm text-gray-500">الكمية: {item.quantity}</p>
                    </div>
                    <div className="text-sm font-medium text-gray-900">
                      <Currency amount={parseFloat(item.display_price) * item.quantity} />
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Promo Code Section */}
              <div className="border-t pt-4 mt-4">
                <div className="space-y-3">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      placeholder="أدخل كود الخصم"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={handleApplyPromo}
                      disabled={!promoCode || applyingPromo}
                      className="px-4 py-2 btn btn-secondary  text-white text-sm rounded-md hover:bg-color-accent disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {applyingPromo ? 'جاري التطبيق...' : 'تطبيق'}
                    </button>
                  </div>
                  
                  {promoError && (
                    <p className="text-sm text-red-600">{promoError}</p>
                  )}
                  
                  {promoDiscount > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-green-600">
                        تم تطبيق كود الخصم: {promoCode}
                      </span>
                      <button
                        type="button"
                        onClick={handleRemovePromo}
                        className="text-red-600 hover:text-red-800"
                      >
                        إزالة
                      </button>
                    </div>
                  )}
                </div>
              </div>

              
              <div className="border-t pt-4 mt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <p>المجموع الفرعي</p>
                  <p><Currency amount={cartSubtotal} /></p>
                </div>
                
                {promoDiscount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <p>الخصم</p>
                    <p>-<Currency amount={promoDiscount} /></p>
                  </div>
                )}
                
                <div className="flex justify-between text-sm">
                  <p>الشحن</p>
                  <p><Currency amount={shippingAmount} /></p>
                </div>
                
                <div className="flex justify-between text-base font-medium text-gray-900 border-t pt-2">
                  <p>المجموع النهائي</p>
                  <p><Currency amount={finalTotal} /></p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Checkout Form */}
        <div className="lg:col-span-7">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Customer Information */}
            <div className="checkout-section-card">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  معلومات العميل
                </h2>
              </div>
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="form-label">الاسم الكامل</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`input ${formErrors.name ? 'border-red-500' : ''}`}
                      required
                    />
                    {formErrors.name && (
                      <p className="text-sm text-red-600 mt-1">{formErrors.name}</p>
                    )}
                  </div>
                  <div>
                    <label className="form-label">البريد الإلكتروني</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`input ${formErrors.email ? 'border-red-500' : ''}`}
                      required
                    />
                    {formErrors.email && (
                      <p className="text-sm text-red-600 mt-1">{formErrors.email}</p>
                    )}
                  </div>
                  <div>
                    <label className="form-label">رقم الهاتف</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`input ${formErrors.phone ? 'border-red-500' : ''}`}
                      required
                    />
                    {formErrors.phone && (
                      <p className="text-sm text-red-600 mt-1">{formErrors.phone}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="checkout-section-card">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <MapPin className="h-5 w-5 mr-2" />
                  عنوان الشحن
                </h2>
              </div>
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="form-label">العنوان</label>
                    <input
                      type="text"
                      name="shipping_address"
                      value={formData.shipping_address}
                      onChange={handleInputChange}
                      className={`input ${formErrors.shipping_address ? 'border-red-500' : ''}`}
                      required
                    />
                    {formErrors.shipping_address && (
                      <p className="text-sm text-red-600 mt-1">{formErrors.shipping_address}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div>
                      <label className="form-label">الدولة</label>
                      <select
                        name="country_id"
                        value={formData.country_id}
                        onChange={handleInputChange}
                        className={`input ${formErrors.country_id ? 'border-red-500' : ''}`}
                        required
                        disabled={loadingCountries}
                      >
                        <option value="">اختر الدولة</option>
                        {countries.map((country) => (
                          <option key={country.id} value={country.id}>{country.name}</option>
                        ))}
                      </select>
                      {formErrors.country_id && (
                        <p className="text-sm text-red-600 mt-1">{formErrors.country_id}</p>
                      )}
                      {loadingCountries && (
                        <p className="text-sm text-gray-500 mt-1">جاري تحميل الدول...</p>
                      )}
                    </div>
                    <div>
                      <label className="form-label">المحافظة</label>
                      <select
                        name="governorate_id"
                        value={formData.governorate_id}
                        onChange={handleInputChange}
                        className={`input ${formErrors.governorate_id ? 'border-red-500' : ''}`}
                        required
                        disabled={loadingGovernorates || !formData.country_id}
                      >
                        <option value="">اختر المحافظة</option>
                        {governorates.map((governorate) => (
                          <option key={governorate.id} value={governorate.id}>{governorate.name}</option>
                        ))}
                      </select>
                      {formErrors.governorate_id && (
                        <p className="text-sm text-red-600 mt-1">{formErrors.governorate_id}</p>
                      )}
                      {loadingGovernorates && (
                        <p className="text-sm text-gray-500 mt-1">جاري تحميل المحافظات...</p>
                      )}
                    </div>
                    <div>
                      <label className="form-label">المدينة</label>
                      <select
                        name="city_id"
                        value={formData.city_id}
                        onChange={handleInputChange}
                        className={`input ${formErrors.city_id ? 'border-red-500' : ''}`}
                        required
                        disabled={loadingCities || !formData.governorate_id}
                      >
                        <option value="">اختر المدينة</option>
                        {cities.map((city) => (
                          <option key={city.id} value={city.id}>{city.name}</option>
                        ))}
                      </select>
                      {formErrors.city_id && (
                        <p className="text-sm text-red-600 mt-1">{formErrors.city_id}</p>
                      )}
                      {loadingCities && (
                        <p className="text-sm text-gray-500 mt-1">جاري تحميل المدن...</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="checkout-section-card">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <CreditCard className="h-5 w-5 mr-2" />
                  طريقة الدفع
                </h2>
              </div>
              <div className="px-6 py-4">
                <div className="space-y-3">
                  <div className="flex items-center">
                    <input
                      id="cash_on_delivery"
                      name="payment_method"
                      type="radio"
                      value="cash_on_delivery"
                      checked={formData.payment_method === 'cash_on_delivery'}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <label htmlFor="cash_on_delivery" className="ml-3 block text-sm font-medium text-gray-700">
                      الدفع عند الاستلام
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="upayments"
                      name="payment_method"
                      type="radio"
                      value="upayments"
                      checked={formData.payment_method === 'upayments'}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <label htmlFor="upayments" className="ml-3 block text-sm font-medium text-gray-700">
                      كي نت (UPayments)
                    </label>
                  </div>
                  {formData.payment_method === 'cash_on_delivery' && (
                    <p className="text-sm text-gray-600 mt-2">
                      ادفع عند وصول طلبك إلى عنوانك.
                    </p>
                  )}
                  {formData.payment_method === 'upayments' && (
                    <p className="text-sm text-gray-600 mt-2">
                      ادفع بأمان عبر الإنترنت باستخدام بطاقتك الائتمانية أو المصرفية.
                    </p>
                  )}
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isProcessing}
              className="w-full btn btn-secondary btn-lg"
            >
              {isProcessing ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  جاري معالجة الطلب...
                </>
              ) : (
                <>
                  <CreditCard className="h-5 w-5 mr-2" />
                  تأكيد الطلب
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Checkout
